import { createSelector } from 'reselect';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';

import { getMasterPermissionsToPage, selectIsClaimAllowed } from 'store/auth/selectors';
import { selectFeatureFlagByKey } from 'store/global/selectors';

import { selectLoanApplication } from './loanApplication.selectors';

export const selectLoanPendingChangesMetadata = createSelector(
  selectLoanApplication,
  ({ pendingChanges }) => pendingChanges,
);

export const selectIsLoanChangesNotSaved = createSelector(
  selectLoanPendingChangesMetadata,
  ({ isChangesNotSaved }) => isChangesNotSaved,
);

export const selectIsLoanChangesLoaded = createSelector(
  selectLoanPendingChangesMetadata,
  ({ isChangesLoaded }) => isChangesLoaded,
);

export const selectIsCurrentUserHasPendingChanges = createSelector(
  selectLoanPendingChangesMetadata,
  ({ hasPendingChangesByCurrentUser }) => hasPendingChangesByCurrentUser,
);

/* eslint-disable max-params */
export const selectIsCurrentUserCanChangeLoan = createSelector(
  selectIsLoanChangesNotSaved,
  selectIsCurrentUserHasPendingChanges,
  selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_SAVE_OTHER_USER_CHANGES),
  selectFeatureFlagByKey(FeatureFlag.EnablePartialPendingChanges),
  selectFeatureFlagByKey(FeatureFlag.PendingChangesByUser),
  getMasterPermissionsToPage(PAGE_CODES.LOAN_APPLICATION),
  (
    isLoanChangesNotSaved,
    isCurrentUserHasPendingChanges,
    hasUserPermissionToSaveOtherUsersLoanChanges,
    isPartialPendingChangesEnabled,
    isPendingChangesByUserEnabled,
    { isEditAllowed },
  ) => {
    if (isLoanChangesNotSaved) {
      return isPartialPendingChangesEnabled || isPendingChangesByUserEnabled
        ? isEditAllowed
        : isCurrentUserHasPendingChanges || hasUserPermissionToSaveOtherUsersLoanChanges;
    }

    return true;
  },
);
/* eslint-enable max-params */

const selectIsProgramChangeRequiredFromLoanPendingChanges = createSelector(
  selectLoanPendingChangesMetadata,
  ({ isProgramChangeRequired }) => isProgramChangeRequired,
);

export const selectIsProgramChangeRequired = createSelector(
  selectIsProgramChangeRequiredFromLoanPendingChanges,
  selectLoanApplication,
  (valueFromPendingChanges, { propertyAndLoan }) =>
    !!(valueFromPendingChanges || propertyAndLoan?.summary?.isProgramChangeRequired),
);

export const selectPendingChangesAuthorsFullNames = createSelector(
  selectLoanPendingChangesMetadata,
  ({ pendingChangesAuthors }) =>
    pendingChangesAuthors?.map(({ firstName, lastName }) =>
      [firstName, lastName].filter(Boolean).join(' '),
    ),
);
